import React, { useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { FormikProps } from "formik";
import { SimulatorTextField } from "../fields/SimulatorTextField";
import { extractMeasuringPoints, isCceeMeasurementCsv } from "../bess/bessCsv";
import { BessSubmercado, FormValues } from "../types";
import { EXIGE_MEDICAO } from "../situacaoUsina";

const SUBMERCADOS: BessSubmercado[] = ["SUDESTE", "SUL", "NORDESTE", "NORTE"];

const groupStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "375px",
  height: "80px",
};

// O upload acumula nome do arquivo, aviso de re-envio e erro de leitura do CSV,
// que juntos passam dos 80px e invadiriam a linha de baixo da grid.
const uploadGroupStyle: React.CSSProperties = {
  ...groupStyle,
  height: undefined,
  minHeight: "80px",
};

const readFileAsText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, "UTF-8");
  });

interface BessCsvFieldsProps {
  formik: FormikProps<FormValues>;
}

export const BessCsvFields = ({ formik }: BessCsvFieldsProps) => {
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { medicaoCsv, medicaoCsvFileName } = formik.values;

  // Usina existente ou em expansão só simula bateria sobre a medição real.
  const medicaoObrigatoria =
    formik.values.calc === "APE" &&
    EXIGE_MEDICAO.includes(formik.values.situacaoUsina);

  const measuringPoints = useMemo(
    () => (medicaoCsv ? extractMeasuringPoints(medicaoCsv) : []),
    [medicaoCsv]
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const content = await readFileAsText(file);

    if (!isCceeMeasurementCsv(content)) {
      setFileError(
        'Arquivo inválido: cabeçalho "Ponto de Medição" não encontrado. Envie o relatório de medição por ativo da CCEE.'
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setFileError(null);
    formik.setFieldValue("medicaoCsv", content);
    formik.setFieldValue("medicaoCsvFileName", file.name);
    formik.setFieldValue("pontoMedicaoBess", "");
  };

  const handleRemove = () => {
    setFileError(null);
    formik.setFieldValue("medicaoCsv", "");
    formik.setFieldValue("medicaoCsvFileName", "");
    formik.setFieldValue("pontoMedicaoBess", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Sem wrapper próprio: os campos entram na mesma grid da seção de bateria.
  return (
    <>
      <Form.Group controlId="medicaoCsvUpload" style={uploadGroupStyle}>
        <Form.Label className="simulacao-form-title">
          {medicaoObrigatoria
            ? "Medição horária CCEE"
            : "Medição horária CCEE (opcional)"}{" "}
          {medicaoObrigatoria && <span>*</span>}
        </Form.Label>
        {medicaoCsv === "" ? (
          <>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              onChange={handleFileChange}
              data-testid="bess-csv-input"
              style={{ display: "block", maxWidth: "100%" }}
            />
            {medicaoCsvFileName !== "" && (
              <span
                title={`Esta simulação usou o arquivo ${medicaoCsvFileName}.`}
                style={{ color: "#f91010", fontSize: 12 }}
              >
                Re-envie o CSV para simular no modo horário.
              </span>
            )}
            {fileError && (
              <span style={{ color: "red", fontSize: 13 }}>{fileError}</span>
            )}
            {!fileError && formik.errors.medicaoCsv && (
              <span style={{ color: "red", fontSize: 13 }}>
                {formik.errors.medicaoCsv}
              </span>
            )}
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>📄 {medicaoCsvFileName}</span>
            <button
              type="button"
              onClick={handleRemove}
              aria-label="Remover CSV"
              style={{
                border: 0,
                borderRadius: "50%",
                width: 22,
                height: 22,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        )}
      </Form.Group>

      {/* Submercado e DoD valem também na série sintética. */}
      {measuringPoints.length > 1 && (
        <Form.Group controlId="pontoMedicaoBess" style={groupStyle}>
          <Form.Label className="simulacao-form-title">
            Ponto de medição da bateria <span>*</span>
          </Form.Label>
          <Form.Control
            as="select"
            name="pontoMedicaoBess"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.pontoMedicaoBess}
            isInvalid={
              !!formik.touched.pontoMedicaoBess &&
              !!formik.errors.pontoMedicaoBess
            }
          >
            <option value="" hidden>
              Selecione a unidade
            </option>
            {measuringPoints.map((point) => (
              <option key={point} value={point}>
                {point}
              </option>
            ))}
          </Form.Control>
          <Form.Control.Feedback type="invalid">
            {formik.errors.pontoMedicaoBess}
          </Form.Control.Feedback>
        </Form.Group>
      )}

      <Form.Group controlId="submercado" style={groupStyle}>
        <Form.Label className="simulacao-form-title">
          Submercado <span>*</span>
        </Form.Label>
        <Form.Control
          as="select"
          name="submercado"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.submercado}
          isInvalid={!!formik.touched.submercado && !!formik.errors.submercado}
        >
          <option value="" hidden>
            Selecione uma opção
          </option>
          {SUBMERCADOS.map((submercado) => (
            <option key={submercado} value={submercado}>
              {submercado}
            </option>
          ))}
        </Form.Control>
        <Form.Control.Feedback type="invalid">
          {formik.errors.submercado}
        </Form.Control.Feedback>
      </Form.Group>

      <SimulatorTextField
        formik={formik}
        name="dod"
        label={
          <>
            DoD — profundidade de descarga <span>*</span>
          </>
        }
        placeholder="Porcentagem"
        unit="%"
      />
    </>
  );
};
